import { useNavigate, useSearch } from '@tanstack/react-router';
import { EmptyState } from '@/components/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ACCESS_TAB_INTRO, ACCESS_TABS, VISIBLE_ACCESS_TABS, resolveAccessTab, tabSearchValue, type AccessTab } from '@/features/access/accessTabModel';
import { MembersPanel } from '@/features/members/MembersPanel';
import { useCan, useFeature, type Can } from '@/lib/permissions';

export interface AccessSearch {
  /** `members` (default). `tokens` and `ci` come back with docs/mvp-plan.md P17. */
  tab?: string;
}
export { ACCESS_TABS };

const TAB_LABEL: Record<AccessTab, string> = { members: 'Members', tokens: 'API tokens', ci: 'CI trust (OIDC)' };

/**
 * Access (screen 30): who may use Hangar. Members (people, roles and invitations) is the first and, in the MVP, the only
 * tab; API tokens, service tokens and CI trust (OIDC) return with docs/mvp-plan.md P17 (VISIBLE_ACCESS_TABS). The tab
 * lives in the URL (`/access?tab=…`) and the header line follows it.
 */
export function AccessPage() {
  const search = useSearch({ strict: false }) as AccessSearch;
  const navigate = useNavigate();
  const tab = resolveAccessTab(search.tab);
  const setTab = (next: string) => void navigate({ to: '/access', search: { tab: tabSearchValue(next) }, replace: true });
  return (
    <div data-testid="access-page" className="flex min-w-0 flex-col gap-5">
      <AccessHeader tab={tab} />
      <Tabs value={tab} onValueChange={setTab} className="gap-5">
        {/* One tab needs no tab bar; the bar comes back with the P17 tabs. */}
        {VISIBLE_ACCESS_TABS.length > 1 ? <AccessTabsList /> : null}
        <TabsContent value="members">
          <MembersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccessHeader({ tab }: { tab: AccessTab }) {
  return (
    <header className="enter flex flex-col gap-1 border-b pb-5">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Access</h1>
      <p data-testid="access-intro" className="max-w-[72ch] text-sm text-muted-foreground">
        {ACCESS_TAB_INTRO[tab]}
      </p>
    </header>
  );
}

function AccessTabsList() {
  return (
    <TabsList variant="line" aria-label="Access sections">
      {VISIBLE_ACCESS_TABS.map((t) => (
        <TabsTrigger key={t} value={t} data-testid={`access-tab-${t}`}>
          {TAB_LABEL[t]}
        </TabsTrigger>
      ))}
    </TabsList>
  );
}

/** Members: open to admins when the API serves members (`features.members`); everyone else learns who can help. */
function useMembersGate(): { served: boolean | undefined; can: Can } {
  return { served: useFeature('members'), can: useCan('admin') };
}

function MembersTab() {
  const { served, can } = useMembersGate();
  if (served === false) return <EmptyState testId="members-unavailable" icon="group" title="Members are not available yet" description="This Hangar does not manage members and invitations yet." />;
  if (!can.allowed) return <EmptyState testId="members-admins-only" icon="lock" title="Only admins manage members" description="Ask an admin of your organization to invite someone, change a role or remove access." />;
  return <MembersPanel />;
}
